/**
 * Générateur — Automatisme : prendre une fraction simple d'un nombre :
 * le 1/3 de 18 ; le 1/4 de 12.
 * Attendu ID : 1055 | Niveau : 5ème | Domaine : Nombres rationnels
 * Rubrique : AUTOMATISME (programme 2025, cycle 4 page 8, ligne 567).
 * Cadrage rédigé par le sous-agent programme-maths (épreuve de Musk incluse,
 * deux questions instruites par le web : Eduscol, DEPP), validé par le prof
 * le 2026-09-09.
 *
 * Le geste : devant « le 1/n de N », N dans la table de n, écrire d'un regard
 * N ÷ n grâce au fait de table n × ? = N, sans poser de calcul et sans passer
 * par « 1/n × N » (écriture de 4e). La fraction est l'opérateur unitaire du
 * CM1, revenu sur un nombre nu.
 *
 * Plan de cartes validé, deux cartes :
 *   - Série flash                 (4 lignes « le 1/n de N = ⬚ », saisie)
 *   - Je vérifie avec la table    (QCM « Combien vaut le 1/n de N ? » 4
 *                                  nombres, Vrai/Faux 3 affirmations)
 * Supprimés à l'épreuve de Musk (cadrage) : carte préalable, QCM « Quel
 * calcul ? » (la division toujours bonne, pur rappel), Relier, drag, trous
 * inverses, ligne en toutes lettres. Le QCM de résultat, écarté au cadrage
 * comme redite, a été ajouté le jour même : règle générale « un QCM par
 * attendu », seul format dépouillable en mode classe QR code.
 *
 * ── Domaine ──────────────────────────────────────────────────────────────
 * n de 3 à 9 (les tables ; 1/2 est « 50 % d'un nombre », l'automatisme
 * suivant, et se fait par la numération, pas par les tables : Eduscol 4e
 * 2024 q.2), N = n × q avec q de 2 à 9, q ≠ n (le nombre recopié serait la
 * réponse), donc N de 6 à 81. Résultat q entier, unique. Jamais de
 * numérateur ≥ 2 (automatisme de 6e, gen_1014, puis 4e), jamais de résultat
 * non entier (objectif, gen_626). Fraction empilée en KaTeX, nombre nu.
 * SACRIFIÉ : (3, 18), « le 1/3 de 18 », l'exemple du programme et de la
 * fiche, exclu au tirage, auto-testé.
 *
 * ── Distracteurs (familles nommées, les mêmes partout) ───────────────────
 *   soustraire        : N − n (le 1/3 de 18 = 15), « prendre » lu « enlever »
 *                       — L'ERREUR PRINCIPALE, décision d'expérience du prof
 *                       (conception additive attestée par la DEPP, jamais
 *                       mesurée sur cette forme) : à vérifier sur les
 *                       réponses réelles ;
 *   mauvais_diviseur  : N ÷ m, m ≠ n (le 1/3 de 60 = 15, ÷ 4 ; le 1/4 de
 *                       12 = 6, réflexe de la moitié) — la seule famille
 *                       retenue par l'évaluation nationale 4e 2024 (« au
 *                       tiers de 60 km ») ;
 *   voisin_meme_table : q ± 1 (le 1/4 de 28 = 6 ou 8), même nom que 1047 ;
 *   fois_denominateur : N × n (le 1/3 de 18 = 54), « de » lu « fois » ;
 *                       secondaire (3,3 % mesurés sur la moitié, DEPP 6e
 *                       2025), jamais seule fausse d'un tableau (la taille
 *                       l'élimine sans le geste), une fois par tableau.
 * Écartées : rien_fait, diviser_par_numerateur, complement, inversion,
 * addition, denominateur_recopie (traitée par construction, q ≠ n).
 * Aucune famille n'est imposée sur une fausse (leçon de 1054 : une famille
 * forcée à forme visible donne un tableau « vrai par structure »).
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou,
} from '../../../utils';

const ATTENDU_ID = 1055;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_FLASH = 'Série flash';
const L_VERIF = 'Je vérifie avec la table';

// ─── Le domaine ───────────────────────────────────────────────────────────

const N_MIN = 3;
const N_MAX = 9;
const Q_MIN = 2;
const Q_MAX = 9;

/** L'exemple du programme et de la fiche, sacrifié : le 1/3 de 18. */
export const SACRIFIE = { n: 3, N: 18 };

const valide = (n, q) => n >= N_MIN && n <= N_MAX && q >= Q_MIN && q <= Q_MAX && q !== n && !(n === SACRIFIE.n && n * q === SACRIFIE.N);

/** Un item : { n, q, N } ; jette hors domaine (replis vérifiés à la main). */
const item = (n, q) => {
    if (!valide(n, q)) throw new Error(`gen_1055 : item hors domaine, le 1/${n} de ${n * q}`);
    return { n, q, N: n * q };
};

/** Un item au hasard, dénominateur imposable, dénominateurs exclus. */
const tirerItem = ({ n = null, exclusN = [] } = {}) => {
    for (let essai = 0; essai < 200; essai++) {
        const nn = n ?? randInt(N_MIN, N_MAX);
        const q = randInt(Q_MIN, Q_MAX);
        if (exclusN.includes(nn) || !valide(nn, q)) continue;
        return item(nn, q);
    }
    return null;
};

/** Replis déterministes vérifiés : 1/4 de 28, 1/7 de 21, 1/3 de 24, 1/9 de 45. */
const REPLIS = [item(4, 7), item(7, 3), item(3, 8), item(9, 5)];

// ─── Écritures ────────────────────────────────────────────────────────────

/** « le 1/n de N », la fraction empilée en KaTeX inline (sans trou : mode texte à l'écran). */
const fractionDe = (it, majuscule = false) => `${majuscule ? 'Le' : 'le'} $\\dfrac{1}{${it.n}}$ de ${it.N}`;

const EXPLICATIONS = {
    soustraire: 'Prendre le 1/3, ce n\'est pas enlever 3 : c\'est partager en 3 parts égales et en prendre une.',
    mauvais_diviseur: 'Le dénominateur dit en combien de parts je partage : le 1/3, c\'est 3 parts, pas 4 ni 6.',
    voisin_meme_table: 'Je vérifie avec la table : 4 × 7 = 28, donc le 1/4 de 28, c\'est 7.',
    fois_denominateur: 'Le 1/3 de 18 est plus petit que 18 : on partage, on ne multiplie pas par 3.',
    regle: 'Le 1/n de N : je partage N en n parts égales, une part vaut N ÷ n. Je vérifie avec la table : n × résultat = N.',
};

// ─── Distracteurs ─────────────────────────────────────────────────────────

/**
 * Les candidats d'un item : valeurs entières > 0, différentes de q, de N et
 * de n (une fausse « le 1/9 de 27, c'est 9 » serait la conception « dénominateur
 * recopié », écartée au cadrage ; revue 2026-09-09, elle revenait par
 * mauvais_diviseur quand m = q et donnait 60 % à « répète n → faux »).
 */
const tousCandidats = (it) => {
    const c = [];
    const pousser = (code, val) => { if (Number.isInteger(val) && val > 0 && val !== it.q && val !== it.N && val !== it.n) c.push({ code, val }); };
    pousser('soustraire', it.N - it.n);
    for (let m = 2; m <= 9; m++) if (m !== it.n && m !== it.q && it.N % m === 0) pousser('mauvais_diviseur', it.N / m);
    for (const dv of [-1, 1]) pousser('voisin_meme_table', it.q + dv);
    pousser('fois_denominateur', it.N * it.n);
    return c;
};

/** Une variante au hasard par famille (Vrai/Faux). */
const candidats = (it) => {
    const parCode = new Map();
    for (const x of shuffle(tousCandidats(it))) if (!parCode.has(x.code)) parCode.set(x.code, x);
    return [...parCode.values()];
};

/**
 * QCM : trois familles distinctes, choisies pour que le RANG de la clé parmi
 * les quatre nombres (plus petite … plus grande) soit tiré parmi les rangs
 * que les conceptions permettent. Presque toutes les erreurs donnent PLUS que
 * la clé (N − n, N × n, N ÷ m avec m < n) : sans sélection la clé serait
 * presque toujours la plus petite. Tirage pondéré vers les rangs rares ;
 * mesuré à la revue. null si moins de trois familles.
 */
const distracteursQcm = (it) => {
    const cands = tousCandidats(it);
    const parRang = new Map();
    for (let i = 0; i < cands.length; i++) {
        for (let j = i + 1; j < cands.length; j++) {
            for (let k = j + 1; k < cands.length; k++) {
                const t = [cands[i], cands[j], cands[k]];
                if (new Set(t.map((x) => x.code)).size < 3 || new Set(t.map((x) => x.val)).size < 3) continue;
                const rang = t.filter((x) => x.val < it.q).length;
                if (!parRang.has(rang)) parRang.set(rang, []);
                parRang.get(rang).push(t);
            }
        }
    }
    if (!parRang.size) return null;
    // Seul voisin_meme_table (q − 1) donne presque toujours moins que la clé,
    // mauvais_diviseur (m > n) parfois : la clé est l'un des deux plus petits
    // nombres dans plus de 90 % des tirages, c'est la nature des erreurs.
    // Le rang 1 manque une fois sur six (q − 1 = n, exclu) : poids 7 / 10 pour
    // égaliser les rangs 0 et 1 sur le parc, les rangs 2 et 3 dès qu'ils existent.
    // Mesuré à la revue (5 000 tirages) : plus petite 46 %, 2e 50 %, 3e 4 % ;
    // sans sélection la clé était la plus petite dans presque tous les cas.
    // La clé reste l'un des deux plus petits nombres : résidu structurel assumé
    // (un élève qui écarte les deux grands joue à pile ou face).
    const POIDS = { 0: 7, 1: 10, 2: 30, 3: 30 };
    const urne = [...parRang.keys()].flatMap((r) => Array(POIDS[r]).fill(r));
    return randomChoice(parRang.get(randomChoice(urne)));
};

// ─── Carte 1 : Série flash (4 lignes, saisie de l'entier) ─────────────────

const CONSIGNE_FLASH = '<p><b>Complète.</b></p>';

const ligneFlash = (it) => `${fractionDe(it)} = {{${it.q}}}`;
const phraseFlash = (items) => [items.slice(0, 2), items.slice(2, 4)].map((paire) => paire.map(ligneFlash).join(' ; ')).join('\n');

const payloadFlash = (items) => {
    const payload = makeTexteTrou(phraseFlash(items), CONSIGNE_FLASH, { ...opts(L_FLASH), mode: 'saisie', strict: true });
    payload.contenu = { ...payload.contenu, explications: { regle: EXPLICATIONS.regle, soustraire: EXPLICATIONS.soustraire } };
    payload._meta.params = { items };
    return payload;
};

const genFlash = () => {
    for (let essai = 0; essai < 200; essai++) {
        const items = [];
        for (let k = 0; k < 4; k++) {
            const it = tirerItem({ exclusN: items.map((x) => x.n) });
            if (!it) break;
            items.push(it);
        }
        if (items.length < 4) continue;
        // un exemple du programme (3 ou 4) et une table qui ne se voit pas d'un coup (6 à 9) ; quatre résultats
        // distincts et quatre nombres distincts (le 1/5 de 30 puis le 1/6 de 30 : le même fait de table deux fois, A8)
        if (!items.some((it) => it.n <= 4) || !items.some((it) => it.n >= 6)) continue;
        if (new Set(items.map((it) => it.q)).size < 4 || new Set(items.map((it) => it.N)).size < 4) continue;
        return payloadFlash(shuffle(items));
    }
    return payloadFlash(REPLIS);
};

// ─── Carte 2, format A : QCM, quatre nombres ──────────────────────────────
// Ajouté après le cadrage (règle générale 2026-09-09 : un QCM par attendu,
// seul format dépouillable en mode classe QR code).

const genQcm = () => {
    for (let essai = 0; essai < 200; essai++) {
        const it = tirerItem();
        if (!it) continue;
        const faux = distracteursQcm(it);
        if (!faux) continue;
        const bon = String(it.q);
        const choix = [bon, ...faux.map((f) => String(f.val))];
        if (new Set(choix).size !== 4) continue;
        const texte = `<p><b>Combien vaut ${fractionDe(it)} ?</b></p>`;
        const payload = makeQcm(choix, bon, texte, opts(L_VERIF));
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        payload._meta.params = { ...it, distracteurs: faux.map(({ code }) => ({ code })) };
        return payload;
    }
    // Repli déterministe vérifié : le 1/4 de 28 = 7 ; soustraire 24, mauvais_diviseur 14 (÷ 2), voisin 6.
    const it = REPLIS[0];
    const payload = makeQcm(['7', '24', '14', '6'], '7', `<p><b>Combien vaut ${fractionDe(it)} ?</b></p>`, opts(L_VERIF));
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    payload._meta.params = { ...it, distracteurs: [{ code: 'soustraire' }, { code: 'mauvais_diviseur' }, { code: 'voisin_meme_table' }] };
    return payload;
};

// ─── Carte 2, format B : Vrai ou faux (3 affirmations) ────────────────────

/** « Le 1/n de N, c'est v. », vraie (v = q) ou fausse (v d'une famille). */
const affirmation = (it, vraie) => {
    if (vraie) return { ligne: `${fractionDe(it, true)}, c'est ${it.q}.`, code: null };
    const cands = candidats(it);
    if (!cands.length) return null;
    const d = randomChoice(cands);
    return { ligne: `${fractionDe(it, true)}, c'est ${d.val}.`, code: d.code };
};

const genVf = () => {
    for (let essai = 0; essai < 200; essai++) {
        const items = [];
        for (let k = 0; k < 3; k++) {
            const it = tirerItem({ exclusN: items.map((x) => x.n) });
            if (!it) break;
            items.push(it);
        }
        if (items.length < 3) continue;
        const nbVraies = randomChoice([1, 2]);
        const verites = shuffle([...Array(nbVraies).fill(true), ...Array(3 - nbVraies).fill(false)]);
        const affs = items.map((it, i) => affirmation(it, verites[i]));
        if (affs.some((a) => !a)) continue;
        const codes = affs.filter((a) => a.code).map((a) => a.code);
        // fois_denominateur : jamais seule fausse (la taille l'élimine sans le geste), une fois au plus
        const nbFois = codes.filter((c) => c === 'fois_denominateur').length;
        if (nbFois > 1 || (nbFois === 1 && codes.length === 1)) continue;
        const lignes = affs.map((a) => a.ligne);
        if (new Set(lignes).size < 3) continue;
        const payload = makeVraiFaux(lignes, verites.map((v) => (v ? 0 : 1)), '<p><b>Vrai ou faux ?</b></p>', opts(L_VERIF));
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        payload._meta.params = { items, verites, distracteurs: codes.map((code) => ({ code })) };
        return payload;
    }
    // Repli déterministe : le 1/4 de 28, c'est 7 (vrai) ; le 1/7 de 21, c'est 14 (faux, soustraire) ; le 1/3 de 24, c'est 6 (faux, mauvais_diviseur ÷ 4).
    const items = REPLIS.slice(0, 3);
    const lignes = [
        `${fractionDe(items[0], true)}, c'est 7.`,
        `${fractionDe(items[1], true)}, c'est 14.`,
        `${fractionDe(items[2], true)}, c'est 6.`,
    ];
    const payload = makeVraiFaux(lignes, [0, 1, 1], '<p><b>Vrai ou faux ?</b></p>', opts(L_VERIF));
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    payload._meta.params = { items, verites: [true, false, false], distracteurs: [{ code: 'soustraire' }, { code: 'mauvais_diviseur' }] };
    return payload;
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'n de 3 à 9, q de 2 à 9, q ≠ n, N = n × q ; le 1/3 de 18 sacrifié ; jamais 1/2 (« 50 % d\'un nombre »), jamais de numérateur ≥ 2, jamais de résultat non entier',
        'chaque distracteur est un entier > 0, différent de q et de N ; aucune famille imposée sur une fausse (leçon 1054)',
        'chaque distracteur porte son code dans _meta.params.distracteurs',
    ],
    verif_qcm_unique: {
        support: '« Combien vaut le 1/n de N ? », quatre nombres ; ajouté après le cadrage (un QCM par attendu, mode classe QR code)',
        rotation: 'trois familles distinctes parmi soustraire, mauvais_diviseur, voisin_meme_table, fois_denominateur, choisies pour que le rang de la clé parmi les quatre nombres soit tiré parmi les rangs possibles (presque toutes les erreurs dépassent la clé) ; résidu mesuré à la revue',
    },
    verif_tableau_vf: {
        support: '« Le 1/n de N, c\'est v. », trois n distincts, 1 ou 2 vraies',
        familles: {
            soustraire: 'N − n, l\'erreur principale (décision d\'expérience du prof, à vérifier sur les réponses réelles)',
            mauvais_diviseur: 'N ÷ m, m ≠ n divisant N (seule famille de l\'évaluation nationale 4e 2024 sur « le tiers de 60 »)',
            voisin_meme_table: 'q ± 1',
            fois_denominateur: 'N × n, secondaire : jamais seule fausse, une fois par tableau au plus',
        },
    },
    flash_texte_trou_saisie: 'quatre lignes, quatre n distincts dont un dans {3, 4} et un dans {6, 7, 8, 9}, quatre résultats distincts ; saisie stricte de l\'entier',
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    flash_texte_trou_saisie: genFlash,
    verif_qcm_unique: genQcm,
    verif_tableau_vf: genVf,
};

export const CATEGORIES = [
    { id: 'flash', label: L_FLASH, formatsList: [{ key: 'flash_texte_trou_saisie', label: 'Saisie trous' }] },
    { id: 'verif', label: L_VERIF, formatsList: [
        { key: 'verif_qcm_unique', label: 'QCM' },
        { key: 'verif_tableau_vf', label: 'Vrai/Faux' },
    ] },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
